import { useEffect, useState } from "react";
import { Map, Marker, useMap } from "@vis.gl/react-google-maps";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/pages/app/AppLayout";
import { useWorkplaces } from "@/pages/app/Ops";
import { useAuth } from "@/context/AuthContext";
import { Circle, MAPS_KEY } from "@/components/app/WorkplaceMap";
import { api, formatError } from "@/lib/api";

const SYDNEY = { lat: -33.8688, lng: 151.2093 };
const LABEL = { inside: "on site", unknown: "location off", not_required: "no check needed" };
const TONE = { inside: "text-emerald-700", outside: "text-red-600", unknown: "text-amber-700", not_required: "text-slate-500" };

const FitTo = ({ points }) => {
  const map = useMap();
  const sig = points.map((p) => `${p.lat},${p.lng}`).join("|");
  useEffect(() => {
    if (!map || !points.length || !window.google) { return; }
    const b = new window.google.maps.LatLngBounds();
    points.forEach((p) => b.extend(p));
    map.fitBounds(b, 60);
    if (points.length === 1) { map.setZoom(16); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, sig]);
  return null;
};

const Focus = ({ pos }) => {
  const map = useMap();
  useEffect(() => {
    if (map && pos) { map.panTo(pos); map.setZoom(17); }
  }, [map, pos]);
  return null;
};

export default function GpsPage() {
  const tz = useAuth().business?.timezone || "Australia/Sydney";
  const [day, setDay] = useState(() => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date()));
  const [events, setEvents] = useState([]);
  const [focus, setFocus] = useState(null);
  const wps = useWorkplaces();
  useEffect(() => {
    api.get("/gps/events", { params: { day } }).then((r) => setEvents(r.data)).catch((e) => toast.error(formatError(e)));
  }, [day]);
  const located = events.filter((e) => e.lat != null);
  const pinned = wps.filter((w) => w.lat != null);
  return (
    <div data-testid="gps-page">
      <PageHeader eyebrow="GPS tracking" title="Clock-in locations">
        <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44 rounded-xl" data-testid="gps-day-input" />
      </PageHeader>
      <p className="mb-4 max-w-2xl text-sm text-slate-500">Clockaroo records a location only at the moment someone clocks in or out, never in the background. Green circles show each workplace's geofence.</p>
      <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
        {MAPS_KEY ? (
          <div className="h-[460px] overflow-hidden rounded-2xl border border-slate-200" data-testid="gps-map">
            <Map defaultCenter={pinned[0] ? { lat: pinned[0].lat, lng: pinned[0].lng } : SYDNEY} defaultZoom={13} gestureHandling="greedy" disableDefaultUI zoomControl>
              {pinned.map((w) => <Circle key={w.workplace_id} center={{ lat: w.lat, lng: w.lng }} radius={w.geofence_radius_m} />)}
              {located.map((e) => <Marker key={e.event_id} position={{ lat: e.lat, lng: e.lng }} title={`${e.name} · ${e.type.replace("_", " ")}`} label={{ text: (e.name || "?")[0], color: "#fff", fontWeight: "700" }} />)}
              <FitTo points={[...located, ...pinned].map((p) => ({ lat: p.lat, lng: p.lng }))} />
              <Focus pos={focus} />
            </Map>
          </div>
        ) : <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">Google Maps key is not configured.</div>}
        <div className="rounded-2xl border border-slate-200 bg-white" data-testid="gps-events-list">
          {events.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="gps-empty">No clock-ins or clock-outs on this day.</p>}
          <ul className="divide-y divide-slate-100">{events.map((e) => (
            <li key={e.event_id}>
              <button disabled={e.lat == null} onClick={() => setFocus({ lat: e.lat, lng: e.lng })} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-slate-50 disabled:cursor-default" data-testid={`gps-event-${e.event_id}`}>
                <span><span className="font-medium">{e.name}</span> <span className="text-slate-500">· {e.type === "clock_in" ? "Clock in" : "Clock out"} · {e.workplace}</span></span>
                <span className="text-right text-xs"><span className="font-mono-num block">{new Date(e.server_time).toLocaleTimeString("en-AU", { timeZone: tz, hour: "2-digit", minute: "2-digit" })}</span>
                  <span className={TONE[e.location?.status] || "text-slate-500"}>{e.location?.status === "outside" ? `${Math.round(e.location.distance_m)} m away` : LABEL[e.location?.status] || (e.lat == null ? "no location" : "recorded")}</span></span>
              </button>
            </li>))}</ul>
        </div>
      </div>
    </div>
  );
}
